import { useState } from "react";
import { ChevronDown } from "lucide-react";

const faqs = [
  {
    question: "Is it safe to buy property in Nigeria from abroad?",
    answer: "Yes, but only if proper legal due diligence is conducted. Many investors lose money because property titles are not verified before payment. A property lawyer can investigate ownership records, confirm title authenticity, and ensure the transaction is legally protected."
  },
  {
    question: "What is property due diligence?",
    answer: "Property due diligence is a legal investigation conducted before purchasing property. It involves reviewing ownership history, verifying title documents, confirming land registry records, and identifying any legal encumbrances or disputes affecting the property."
  },
  {
    question: "How can I verify land title in Nigeria?",
    answer: "Title verification involves searching government land registries and confirming the authenticity of documents such as Certificates of Occupancy, Deeds of Assignment, or Governor's Consent. This process ensures the seller has legal authority to transfer ownership."
  },
  {
    question: "Do I need a lawyer when buying property in Nigeria?",
    answer: "Yes. A property lawyer protects your interests by conducting due diligence, reviewing contracts, ensuring proper documentation, and guiding the transaction through legal completion."
  },
  {
    question: "Can Nigerians in the diaspora safely invest in Nigerian real estate?",
    answer: "Yes. With proper legal advisory and due diligence, diaspora investors can safely acquire property in Nigeria. Professional legal oversight helps ensure the transaction is transparent, documented, and protected from fraud."
  }
];

const FAQItem = ({ question, answer, isOpen, onToggle, index }) => {
  return (
    <div 
      className="border-b border-[#D4AF37]/20 last:border-b-0"
      data-testid={`faq-item-${index}`}
    >
      <button
        onClick={onToggle}
        className="w-full py-6 md:py-8 flex items-start justify-between gap-4 text-left group"
        aria-expanded={isOpen}
        data-testid={`faq-toggle-${index}`}
      >
        <span className="font-['Playfair_Display'] font-semibold text-base md:text-lg lg:text-xl text-white group-hover:text-[#D4AF37] transition-colors pr-4">
          {question}
        </span>
        <div className={`w-8 h-8 md:w-10 md:h-10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0 transition-all duration-300 ${isOpen ? 'bg-[#D4AF37] border-[#D4AF37]' : 'group-hover:border-[#D4AF37]'}`}>
          <ChevronDown 
            className={`w-4 h-4 md:w-5 md:h-5 transition-transform duration-300 ${isOpen ? 'rotate-180 text-[#0A0A0B]' : 'text-[#D4AF37]'}`} 
          />
        </div>
      </button>
      <div 
        className={`overflow-hidden transition-all duration-500 ease-in-out ${isOpen ? 'max-h-[500px] pb-6 md:pb-8' : 'max-h-0'}`}
      >
        <p className="text-white/60 text-sm md:text-base leading-[1.9] md:leading-[2] pr-12 md:pr-16">
          {answer}
        </p>
      </div>
    </div>
  );
};

export const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  return (
    <section 
      className="py-16 md:py-24 lg:py-32 bg-[#0A0A0B] relative overflow-hidden"
      data-testid="faq-section"
    >
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
      
      {/* Decorative corner accents */}
      <div className="absolute top-8 left-8 w-16 h-16 border-l border-t border-[#D4AF37]/20 hidden lg:block" />
      <div className="absolute bottom-8 right-8 w-16 h-16 border-r border-b border-[#D4AF37]/20 hidden lg:block" />
      
      <div className="relative z-10 max-w-4xl mx-auto px-5 md:px-8 lg:px-12">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14 lg:mb-16">
          <span className="text-[#D4AF37] text-xs md:text-sm tracking-[0.2em] md:tracking-[0.3em] uppercase font-semibold">
            Common Questions
          </span>
          <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-white mt-4 md:mt-6 leading-tight">
            Frequently Asked Questions
            <span className="block text-[#D4AF37] italic">for Property Investors</span>
          </h2>
          <div className="w-16 md:w-20 h-px bg-[#D4AF37] mx-auto mt-6 md:mt-8" />
        </div>

        {/* FAQ Accordion */}
        <div className="border-t border-[#D4AF37]/20">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              index={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onToggle={() => handleToggle(index)}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-10 md:mt-14 pt-8 md:pt-10 border-t border-[#D4AF37]/20">
          <p className="text-white/50 text-sm md:text-base mb-4 md:mb-6">
            Have more questions about property investment in Nigeria?
          </p>
          <a
            href="#booking"
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0B] px-6 md:px-8 py-3 md:py-4 font-semibold text-sm tracking-wide uppercase hover:bg-[#C5A028] transition-colors"
            data-testid="faq-cta-button"
          >
            Book a Consultation
          </a>
        </div>
      </div>
    </section>
  );
};
